import { ITEM_TYPES } from '@brickloop/core/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, qs, type ChannelStatus, type Paged } from '../api';
import { isOwner, useAuth } from '../auth';
import { BrickowlCard } from '../components/BrickowlCard';
import { Badge, Button, Card, Checkbox, ErrorMessage, Field, Input, Notice, PageHeader, Pagination, Select, Spinner, money, number, plural, unitMoney, when } from '../ui';

interface Listing {
  id: number;
  itemType: string;
  itemNo: string;
  itemName: string | null;
  colorName: string | null;
  condition: string;
  remoteQty: number;
  remotePrice: string | null;
  remoteRemarks: string | null;
  remoteStatus: string;
  state: 'matched' | 'drift' | 'unmatched';
  lotId: number | null;
  localQty: number | null;
  localPrice: string | null;
}

const STATE_TONE = { matched: 'green', drift: 'amber', unmatched: 'blue' } as const;
const STATE_LABEL = { matched: 'Matches', drift: 'Differs', unmatched: 'Not in BrickLoop' } as const;

export function Channels() {
  const { user } = useAuth();
  const owner = isOwner(user);
  const channels = useQuery({ queryKey: ['channels'], queryFn: () => api<{ channels: ChannelStatus[] }>('/api/v1/channels').then((r) => r.channels) });
  if (channels.isPending) return <Spinner />;
  const bl = channels.data?.find((c) => c.kind === 'bricklink');
  const bo = channels.data?.find((c) => c.kind === 'brickowl');

  return (
    <>
      <PageHeader title="Channels" subtitle="Where you sell. Brick Owl is read-only: BrickLoop reads it and cannot change that store." />
      <div className="space-y-4">
        <ErrorMessage error={channels.error} />
        {bl && <BricklinkCard status={bl} owner={owner} />}
        {bo && <BrickowlCard status={bo} owner={owner} />}
        {owner && <CatalogueCard />}
      </div>
    </>
  );
}

function BricklinkCard({ status, owner }: { status: ChannelStatus; owner: boolean }) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['channels'] });
    void qc.invalidateQueries({ queryKey: ['listings'] });
    void qc.invalidateQueries({ queryKey: ['lots'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
    for (const k of ['catalog-status', 'colors', 'categories', 'locations']) void qc.invalidateQueries({ queryKey: [k] });
  };
  const [creds, setCreds] = useState({ consumerKey: '', consumerSecret: '', token: '', tokenSecret: '' });
  // Writing is switched on and off only from the Sync page. This form never changes it: a read-write connection is left as it is.
  const [mode, setMode] = useState<'off' | 'read_only'>(status.mode === 'off' ? 'off' : 'read_only');
  const anyCred = Object.values(creds).some(Boolean);
  const allCred = Object.values(creds).every(Boolean);

  const save = useMutation({
    mutationFn: () => api('/api/v1/channels/bricklink', { method: 'PUT', body: { ...(status.mode === 'read_write' && mode !== 'off' ? {} : { mode }), ...(anyCred ? { credentials: creds } : {}) } }),
    onSuccess: () => {
      setCreds({ consumerKey: '', consumerSecret: '', token: '', tokenSecret: '' });
      refresh();
    },
  });
  const test = useMutation({ mutationFn: () => api<{ detail: string }>('/api/v1/channels/bricklink/test', { method: 'POST', body: {} }), onSuccess: refresh });
  const pull = useMutation({
    mutationFn: () => api<{ result: { fetched: number; skipped: unknown[]; itemsLearned: number; removed: number } }>('/api/v1/channels/bricklink/pull', { method: 'POST', body: {} }),
    onSuccess: refresh,
  });

  return (
    <>
      <Card
        title={
          <span className="flex items-center gap-2">
            BrickLink {status.mode === 'off' ? <Badge>Off</Badge> : status.mode === 'read_write' ? <Badge tone="amber">Can change your store</Badge> : <Badge tone="blue">Read only</Badge>}
            {status.configured ? <Badge tone="green">Credentials saved</Badge> : <Badge tone="amber">No credentials</Badge>}
          </span>
        }
        actions={
          status.configured && owner ? (
            <>
              <Button variant="secondary" small disabled={test.isPending} onClick={() => test.mutate()}>
                {test.isPending ? 'Testing…' : 'Test connection'}
              </Button>
              <Button small disabled={status.mode === 'off' || pull.isPending} title={status.mode === 'off' ? 'Turn the connection on first' : undefined} onClick={() => pull.mutate()}>
                {pull.isPending ? 'Reading…' : 'Read inventory now'}
              </Button>
            </>
          ) : undefined
        }
      >
        <div className="space-y-4">
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-slate-500">Last read</dt>
              <dd>{when(status.lastPullAt)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Requests today</dt>
              <dd>
                {number(status.requestsToday.used)} of {number(status.requestsToday.cap)}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Result</dt>
              <dd className="truncate" title={status.lastPullStatus ?? undefined}>
                {status.lastPullStatus ?? '—'}
              </dd>
            </div>
          </dl>
          <ErrorMessage error={test.error ?? pull.error} />
          {test.data && <Notice tone="green">{test.data.detail}</Notice>}
          {pull.data && (
            <Notice tone="green">
              Read {number(pull.data.result.fetched)} lots.
              {pull.data.result.itemsLearned > 0 && ` Learned ${number(pull.data.result.itemsLearned)} new catalogue items.`}
              {pull.data.result.removed > 0 && ` ${number(pull.data.result.removed)} lots are no longer on BrickLink.`}
              {pull.data.result.skipped.length > 0 && ` ${pull.data.result.skipped.length} skipped.`}
            </Notice>
          )}

          {owner ? (
            <form
              className="space-y-3 border-t border-slate-100 pt-4"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
            >
              <p className="text-sm text-slate-600">
                Get the four values from BrickLink under <em>My Store → API → Register new Consumer</em>, then create the token. BrickLink ties a token to an IP address, so register this server's address (or 0.0.0.0 if it can change). The values are encrypted when saved and are never shown again.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Consumer key">
                  <Input autoComplete="off" value={creds.consumerKey} placeholder={status.configured ? 'unchanged' : ''} onChange={(e) => setCreds({ ...creds, consumerKey: e.target.value })} />
                </Field>
                <Field label="Consumer secret">
                  <Input type="password" autoComplete="new-password" value={creds.consumerSecret} placeholder={status.configured ? 'unchanged' : ''} onChange={(e) => setCreds({ ...creds, consumerSecret: e.target.value })} />
                </Field>
                <Field label="Token value">
                  <Input autoComplete="off" value={creds.token} placeholder={status.configured ? 'unchanged' : ''} onChange={(e) => setCreds({ ...creds, token: e.target.value })} />
                </Field>
                <Field label="Token secret">
                  <Input type="password" autoComplete="new-password" value={creds.tokenSecret} placeholder={status.configured ? 'unchanged' : ''} onChange={(e) => setCreds({ ...creds, tokenSecret: e.target.value })} />
                </Field>
              </div>
              {anyCred && !allCred && <p className="text-xs text-amber-700">Enter all four values to replace the saved ones.</p>}
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Connection">
                  <Select value={mode} onChange={(e) => setMode(e.target.value as 'off' | 'read_only')}>
                    <option value="off">Off</option>
                    <option value="read_only">Read only</option>
                  </Select>
                </Field>
                <Button type="submit" disabled={save.isPending || (anyCred && !allCred)}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
              <ErrorMessage error={save.error} />
            </form>
          ) : (
            <p className="text-sm text-slate-500">Only the owner can change the connection.</p>
          )}
        </div>
      </Card>
      {status.listings.total > 0 && <Listings status={status} onChanged={refresh} />}
    </>
  );
}

function Listings({ status, onChanged }: { status: ChannelStatus; onChanged: () => void }) {
  const [state, setState] = useState<'' | 'matched' | 'drift' | 'unmatched'>(status.listings.unmatched > 0 ? 'unmatched' : '');
  const [page, setPage] = useState(1);
  const [remarksAsLocation, setRemarksAsLocation] = useState(true);
  const list = useQuery({
    queryKey: ['listings', state, page, status.listings],
    queryFn: () => api<Paged<Listing>>(`/api/v1/channels/bricklink/listings${qs({ state, page, pageSize: 25 })}`),
    placeholderData: (p) => p,
  });
  const importAll = useMutation({
    mutationFn: () => api<{ result: { lotsCreated: number; pieces: number; mergedDuplicates: number } }>('/api/v1/channels/bricklink/import-unmatched', { body: { remarksAsLocation } }),
    onSuccess: onChanged,
  });
  const { listings: n } = status;

  return (
    <Card
      title="What is on BrickLink"
      actions={
        <Select aria-label="Show" className="w-48" value={state} onChange={(e) => { setState(e.target.value as typeof state); setPage(1); }}>
          <option value="">All ({number(n.total)})</option>
          <option value="unmatched">Not in BrickLoop ({number(n.unmatched)})</option>
          <option value="drift">Quantity or price differs ({number(n.drift)})</option>
          <option value="matched">Matches ({number(n.matched)})</option>
        </Select>
      }
    >
      <div className="space-y-4">
        {n.unmatched > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-50 p-3 text-sm">
            <div>
              <p className="font-medium">{number(n.unmatched)} BrickLink lots are not in BrickLoop yet.</p>
              <p className="text-slate-600">Bring them in as stock under an “Opening stock” purchase. This only creates local records; BrickLink is not touched.</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Checkbox label="Remarks hold my bin location" checked={remarksAsLocation} onChange={setRemarksAsLocation} />
              <Button disabled={importAll.isPending} onClick={() => importAll.mutate()}>
                {importAll.isPending ? 'Importing…' : 'Bring them in'}
              </Button>
            </div>
          </div>
        )}
        <ErrorMessage error={importAll.error} />
        {importAll.data && (
          <Notice tone="green">
            Created {plural(importAll.data.result.lotsCreated, 'lot')} with {plural(importAll.data.result.pieces, 'piece')}.
            {importAll.data.result.mergedDuplicates > 0 && ` ${plural(importAll.data.result.mergedDuplicates, 'BrickLink lot')} shared an item, colour and condition with another and ${importAll.data.result.mergedDuplicates === 1 ? 'was' : 'were'} combined.`}{' '}
            <Link to="/items?view=lots" className="underline">View inventory</Link>
          </Notice>
        )}
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[44rem] text-sm">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Colour</th>
                    <th>Cond.</th>
                    <th className="text-right">Qty here</th>
                    <th className="text-right">Qty BrickLoop</th>
                    <th className="text-right">Price here</th>
                    <th className="text-right">Price BrickLoop</th>
                    <th>State</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data?.rows.map((l) => (
                    <tr key={l.id}>
                      <td>
                        {l.lotId ? <Link to={`/lots/${l.lotId}`} className="font-medium hover:underline">{l.itemNo}</Link> : <span className="font-medium">{l.itemNo}</span>}
                        <div className="max-w-48 truncate text-xs text-slate-500">{l.itemName}</div>
                      </td>
                      <td>{l.colorName}</td>
                      <td>{l.condition}</td>
                      <td className="text-right tabular-nums">{number(l.remoteQty)}</td>
                      <td className="text-right tabular-nums">{l.localQty === null ? '—' : number(l.localQty)}</td>
                      <td className="text-right tabular-nums">{unitMoney(l.remotePrice)}</td>
                      <td className="text-right tabular-nums">{unitMoney(l.localPrice)}</td>
                      <td>
                        <Badge tone={STATE_TONE[l.state]}>{STATE_LABEL[l.state]}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.data && <Pagination page={page} pageSize={25} total={list.data.total} onPage={setPage} />}
          </>
        )}
      </div>
    </Card>
  );
}

function CatalogueCard() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['catalog-status'], queryFn: () => api<{ counts: { items: number; colors: number; categories: number } }>('/api/v1/catalog/status').then((r) => r.counts) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['catalog-status'] });
    void qc.invalidateQueries({ queryKey: ['colors'] });
    void qc.invalidateQueries({ queryKey: ['categories'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const seed = useMutation({ mutationFn: () => api<{ result: { colors: number; categories: number } }>('/api/v1/catalog/seed-bricklink', { method: 'POST', body: {} }), onSuccess: refresh });

  const [kind, setKind] = useState<string>('PART');
  const [text, setText] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const load = useMutation({
    mutationFn: () => {
      const body = kind === 'colors' || kind === 'categories' ? { kind, content: text } : { kind: 'items', itemType: kind, content: text };
      return api<{ result: { imported: number; skipped: number; categoriesLearned?: number } }>('/api/v1/catalog/import', { body });
    },
    onSuccess: () => {
      refresh();
      setText(null);
      setFileName('');
    },
  });

  return (
    <Card title="Catalogue">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          {status.data ? (
            <>
              {number(status.data.items)} items, {number(status.data.colors)} colours, {number(status.data.categories)} categories.
            </>
          ) : (
            'Loading…'
          )}{' '}
          Items you already sell are added automatically when you read your BrickLink inventory. For everything else, load BrickLink's own catalogue files.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" disabled={seed.isPending} onClick={() => seed.mutate()}>
            {seed.isPending ? 'Loading…' : 'Load colours and categories from BrickLink'}
          </Button>
          <span className="text-xs text-slate-500">Uses 2 of today's requests. Needs the connection above to be set up.</span>
        </div>
        <ErrorMessage error={seed.error} />
        {seed.data && <Notice tone="green">Loaded {seed.data.result.colors} colours and {seed.data.result.categories} categories.</Notice>}

        <form
          className="flex flex-wrap items-end gap-3 border-t border-slate-100 pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            load.mutate();
          }}
        >
          <Field label="Import a BrickLink catalogue file" hint="Download it from BrickLink's catalogue download page, as tab-delimited text.">
            <input type="file" accept=".txt,.tsv,.csv" className="block text-sm" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { setFileName(f.name); setText(await f.text()); } }} />
          </Field>
          <Field label="It contains">
            <Select value={kind} onChange={(e) => setKind(e.target.value)}>
              {ITEM_TYPES.map((t) => (
                <option key={t} value={t}>
                  Items: {t.toLowerCase().replace('_', ' ')}
                </option>
              ))}
              <option value="colors">Colours</option>
              <option value="categories">Categories</option>
            </Select>
          </Field>
          <Button type="submit" disabled={!text || load.isPending}>
            {load.isPending ? 'Importing…' : 'Import'}
          </Button>
        </form>
        <ErrorMessage error={load.error} />
        {load.data && (
          <Notice tone="green">
            Imported {number(load.data.result.imported)} rows from {fileName || 'the file'}.
            {load.data.result.skipped > 0 && ` ${load.data.result.skipped} skipped.`}
            {!!load.data.result.categoriesLearned && ` ${load.data.result.categoriesLearned} categories learned from it.`}
          </Notice>
        )}
      </div>
    </Card>
  );
}
